// ติดตามงานครูประจำชั้นศาสนา — หัวหน้าวิชาการศาสนา (pos 57)
//
// 1 แถวต่อห้อง พร้อมสถานะสามงานที่เป็นของครูประจำชั้นศาสนาจริง ๆ:
//   หอพัก · ขั้นตอนดูแลนักเรียน · แจ้งลา + การเข้าใช้ระบบ
// (เหตุผลว่าทำไมเหลือสามงาน อยู่ที่หัวไฟล์ server/models/islamicAdvisorTrackingModel.js)
//
// อ่านอย่างเดียว — การจัดหอพัก แจ้งลา และติ๊กขั้นตอนดูแล ทำที่เมนูของครูประจำชั้นเหมือนเดิม
// หน้านี้มีไว้ตอบว่า "ห้องไหนยังไม่ได้ทำ" ไม่ใช่ให้หัวหน้าวิชาการทำแทน
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Card, Col, Form, Nav, Table } from 'react-bootstrap'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  API, AcademicFilterBar, InfoTip, StatTile, alertRequestError, roomLabel,
  useAcademicFilters, useDarkTables, useStudyYears,
} from './academicShared'
import IslamicAdvisorTaskDetail from './IslamicAdvisorTaskDetail'

const EMPTY = { rows: [], summary: null, staleDays: 30 }

const DUTIES = ['dorm', 'care', 'leave']

// แถบความคืบหน้าของทะเบียนหอพัก — ตัวเลขนำเสมอ สีเป็นตัวช่วยอ่าน ไม่ใช่ตัวบอกความหมาย
// ตัวเดียว (เหลือง/เขียวบนพื้นสว่างคอนทราสต์ไม่พอสำหรับคนตาบอดสี)
const DormCell = ({ t, dorm }) => {
  if (!dorm.total) return <span className="text-secondary fs-sm">-</span>
  const missing = dorm.total - dorm.recorded
  const pct = Math.round((dorm.recorded / dorm.total) * 100)
  return (
    <div style={{ minWidth: 110 }}>
      <div className="d-flex align-items-center justify-content-between gap-2 fs-xs">
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{dorm.recorded}/{dorm.total}</span>
        {missing === 0
          ? <Badge bg="success" className="fs-xs fw-normal">{t.academic.advisorTasks.dormComplete}</Badge>
          : <Badge bg="danger" className="fs-xs fw-normal">{t.academic.advisorTasks.dormMissing(missing)}</Badge>}
      </div>
      <div className="rounded-pill mt-1" style={{ height: 6, background: 'currentColor', opacity: 0.12 }}>
        <div
          className="rounded-pill"
          style={{ height: 6, width: `${pct}%`, background: missing === 0 ? '#198754' : '#dc3545' }}
        />
      </div>
    </div>
  )
}

// ขั้นตอนดูแล — "ไม่มีคนติดเกณฑ์" กับ "ติดเกณฑ์แต่ทำครบแล้ว" เป็นคนละเรื่อง ห้องแรกไม่มี
// อะไรให้ทำ ห้องหลังครูลงมือไปแล้ว การแสดงเหมือนกันจะกลบงานที่ครูทำไป
const CareCell = ({ t, care }) => {
  if (!care.students) return <span className="text-secondary fs-sm">{t.academic.advisorTasks.careNone}</span>
  if (!care.pending) {
    return (
      <Badge bg="success" className="fs-xs fw-normal">
        {t.academic.advisorTasks.careComplete(care.students)}
      </Badge>
    )
  }
  return (
    <Badge bg="warning" text="dark" className="fs-xs fw-normal">
      {t.academic.advisorTasks.carePending(care.pending, care.students)}
    </Badge>
  )
}

// ครูประจำชั้น + การเข้าใช้ระบบรายคน
//
// ห้องหนึ่งมีครูประจำชั้นได้หลายคน จึงแสดงทุกคน ไม่ยุบเป็นค่าเดียวของห้อง — ห้องที่ครู
// คนหนึ่งยังใช้งานอยู่แต่อีกคนหายไป คือสิ่งที่ต้องเห็น ไม่ใช่สิ่งที่ควรถูกเฉลี่ยทิ้ง
const AdvisorCell = ({ t, advisors, staleDays }) => {
  if (advisors.length === 0) {
    return <Badge bg="secondary" className="fs-xs fw-normal">{t.academic.advisorTasks.noAdvisor}</Badge>
  }
  return (
    <div style={{ minWidth: 0 }}>
      {advisors.map(a => (
        <div key={a.t_id} className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          <span className="fs-sm text-truncate">{a.t_name}</span>
          {a.inactive
            ? <Badge bg="dark" className="fs-xs fw-normal">{t.academic.advisorTasks.inactiveAccount}</Badge>
            : a.last_login == null
              ? <Badge bg="danger" className="fs-xs fw-normal">{t.academic.advisorTasks.neverLogin}</Badge>
              : a.days_since_login >= staleDays
                ? <Badge bg="warning" text="dark" className="fs-xs fw-normal">{t.academic.advisorTasks.loginDays(a.days_since_login)}</Badge>
                : <span className="fs-xs text-secondary">{a.days_since_login === 0
                    ? t.academic.advisorTasks.loginToday
                    : t.academic.advisorTasks.loginDays(a.days_since_login)}</span>}
        </div>
      ))}
    </div>
  )
}

export default function IslamicAdvisorTasks() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  const filters = useAcademicFilters(yearId, t)
  const [data, setData] = useState(EMPTY)
  const [onlyPending, setOnlyPending] = useState(false)
  const [open, setOpen] = useState(null)   // ir_id ของแถวที่กางอยู่ (กางได้ทีละห้อง)
  const [duty, setDuty] = useState('dorm') // แท็บที่เลือกในแถวที่กางอยู่

  useEffect(() => {
    if (!yearId) return
    let alive = true
    setLoading(true)
    setOpen(null)
    axiosInstance.get(`${API}/islamic/academic/advisorTasks/${yearId}${filters.query}`)
      .then(res => { if (alive) setData(res.data || EMPTY) })
      .catch(err => { if (alive) { setData(EMPTY); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [yearId, filters.query, setLoading, t])

  const { rows, summary, staleDays } = data

  // "มีงานค้าง" = หอพักไม่ครบ หรือขั้นดูแลยังไม่ครบ หรือยังไม่มีครูประจำชั้น
  // การเข้าใช้ระบบไม่นับเป็นงานค้างของห้อง — เป็นเรื่องของตัวครู ไม่ใช่ของห้องที่เขาดูแล
  const hasPending = (r) =>
    r.advisors.length === 0
    || (r.dorm.total > 0 && r.dorm.recorded < r.dorm.total)
    || r.care.pending > 0

  const visible = useMemo(
    () => (onlyPending ? rows.filter(hasPending) : rows),
    [rows, onlyPending]
  )

  const toggle = (irId) => {
    if (open === irId) { setOpen(null); return }
    setOpen(irId)
    setDuty('dorm')
  }

  const A = t.academic.advisorTasks

  return (
    <React.Fragment>
      <div className="mb-3">
        <ol className="breadcrumb fs-sm mb-1">
          <li className="breadcrumb-item">{t.academic.groupTitle}</li>
          <li className="breadcrumb-item active">{A.pageTitle}</li>
        </ol>
        <h4 className="main-title mb-0">{A.pageTitle}</h4>
        <div className="fs-sm text-secondary mt-1">{A.subtitle}</div>
      </div>

      <Card className="card-one mb-3">
        <Card.Body>
          <AcademicFilterBar t={t} years={years} yearId={yearId} setYearId={setYearId} filters={filters}>
            <Col xs={12} md={6} lg={3} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="only-pending-advisor-tasks"
                className="fs-sm"
                label={A.onlyPending}
                checked={onlyPending}
                onChange={e => setOnlyPending(e.target.checked)}
              />
            </Col>
          </AcademicFilterBar>
        </Card.Body>
      </Card>

      {summary && (
        <div className="row g-2 mb-3">
          <Col xs={6} md={3}>
            <StatTile
              label={A.statDormMissing}
              value={summary.dormMissing.toLocaleString('th-TH')}
              sub={A.statDormMissingSub(summary.roomsDormPending)}
            />
          </Col>
          <Col xs={6} md={3}>
            <StatTile
              label={A.statCarePending}
              value={summary.carePending.toLocaleString('th-TH')}
              sub={A.statCarePendingSub(summary.roomsCarePending)}
            />
          </Col>
          <Col xs={6} md={3}>
            <StatTile
              label={A.statLeaves}
              value={summary.leaves.toLocaleString('th-TH')}
              sub={A.statLeavesSub}
            />
          </Col>
          <Col xs={6} md={3}>
            <StatTile
              label={A.statStale(staleDays)}
              value={summary.staleLogin.toLocaleString('th-TH')}
              sub={A.statStaleSub(summary.neverLogin, summary.advisors)}
            />
          </Col>
        </div>
      )}

      <Card className="card-one">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2">
          <Card.Title as="h6" className="mb-0">{A.listTitle}</Card.Title>
          <Badge bg="secondary" className="fs-xs">{A.roomCount(visible.length)}</Badge>
          <InfoTip text={A.dormHint} />
        </Card.Header>
        <Card.Body className="p-0">
          {visible.length === 0 ? (
            <div className="text-center text-secondary py-5 fs-sm">
              {rows.length === 0 ? A.empty : A.allDone}
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>{A.colRoom}</th>
                    <th style={{ width: 240 }}>{A.colAdvisor}</th>
                    <th className="text-end" style={{ width: 80 }}>{A.colStudents}</th>
                    <th style={{ width: 150 }}>{A.colDorm}</th>
                    <th style={{ width: 170 }}>{A.colCare}</th>
                    <th className="text-end" style={{ width: 90 }}>{A.colLeave}</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <React.Fragment key={r.ir_id}>
                      <tr
                        className={open === r.ir_id ? 'table-active' : undefined}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggle(r.ir_id)}
                      >
                        <td className="fs-sm fw-semibold">{roomLabel(r)}</td>
                        <td><AdvisorCell t={t} advisors={r.advisors} staleDays={staleDays} /></td>
                        <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r.total_students}
                        </td>
                        <td><DormCell t={t} dorm={r.dorm} /></td>
                        <td><CareCell t={t} care={r.care} /></td>
                        <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r.leave.count || <span className="text-secondary">-</span>}
                        </td>
                        <td className="text-center text-secondary">
                          <i className={`ri-arrow-${open === r.ir_id ? 'down' : 'right'}-s-line`} />
                        </td>
                      </tr>
                      {open === r.ir_id && (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="p-2">
                              <Nav variant="pills" className="gap-1 mb-2">
                                {DUTIES.map(d => (
                                  <Nav.Item key={d}>
                                    <Nav.Link
                                      active={duty === d}
                                      className="py-1 px-2 fs-xs"
                                      onClick={() => setDuty(d)}
                                    >
                                      {A[`tab_${d}`]}
                                    </Nav.Link>
                                  </Nav.Item>
                                ))}
                              </Nav>
                              <IslamicAdvisorTaskDetail t={t} yearId={yearId} irId={r.ir_id} duty={duty} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        <Card.Footer className="fs-xs text-secondary">{A.footerHint}</Card.Footer>
      </Card>
    </React.Fragment>
  )
}
